import type { AnalyticsStatMetadata } from '~/hooks/queries/useAnalyticsStatMetadataQuery';
import type { ErrorObjectString } from '~/utils/typescriptHelpers';

/** Zod validation errors: field -> messages */
export type ZodValidationErrors = Record<string, string[]>;

/** Flat error format from Node API (see errors.md) */
export type AnalyticsStatsErrorResponse = {
  message?: string;
  code?: string;
  details?: Record<string, unknown>;
};

type NestedRunResponse = { success?: boolean; error?: unknown; errors?: unknown };

// ─── Type guards ────────────────────────────────────────────────────────────

function isErrorObjectString(data: object): data is ErrorObjectString {
  return 'error' in data && typeof (data as ErrorObjectString).error === 'string';
}

function isZodValidationErrors(data: object): data is { errors: ZodValidationErrors } {
  if (!('errors' in data)) return false;
  const { errors } = data as { errors: unknown };
  if (!errors || typeof errors !== 'object') return false;
  return Object.values(errors as Record<string, unknown>).every(
    (msgs) => Array.isArray(msgs) && msgs.every((m) => typeof m === 'string'),
  );
}

function isDomainError(data: object): data is AnalyticsStatsErrorResponse {
  return (
    ('message' in data || 'code' in data || 'details' in data) &&
    !isErrorObjectString(data) &&
    !isZodValidationErrors(data)
  );
}

function isNestedRun(data: object): data is NestedRunResponse {
  return 'success' in data && (data as NestedRunResponse).success === false;
}

// ─── Metadata label resolution ──────────────────────────────────────────────

function resolveLabel(
  metadata: AnalyticsStatMetadata | undefined,
  keyOrAlias: string,
  kind: 'grouping' | 'stat' | 'display',
): string {
  if (!metadata) return keyOrAlias;
  if (kind === 'grouping') return metadata.groupingsByKey[keyOrAlias]?.label ?? keyOrAlias;
  if (kind === 'stat') return metadata.statColumnsByAlias[keyOrAlias]?.label ?? keyOrAlias;
  return metadata.displayColumnsByAlias[keyOrAlias]?.label ?? keyOrAlias;
}

function resolveLabels(
  metadata: AnalyticsStatMetadata | undefined,
  keys: string[] | undefined,
  kind: 'grouping' | 'stat' | 'display',
): string[] {
  if (!keys?.length) return [];
  return keys.map((k) => resolveLabel(metadata, k, kind));
}

// ─── Domain error formatters (see errors.md) ──────────────────────────────────

function formatInvalidColumn(d: Record<string, unknown>, m?: AnalyticsStatMetadata): string | null {
  const alias = (d.alias as string) ?? 'column';
  const required = d.allowedWhenGroupByIncludes as string[] | undefined;
  if (required?.length) {
    const kind = d.columnKind === 'stat' ? 'stat' : 'display';
    const label = kind === 'stat' ? 'Metrics' : 'Details';
    const name = resolveLabel(m, alias, kind);
    const list = required.map((k) => resolveLabel(m, k, 'grouping'));
    const requiredList =
      list.length === 1 ? list[0] : `${list.slice(0, -1).join(', ')} or ${list.at(-1)}`;
    return `The ${label} column "${name}" requires one of these Dimensions to be selected: ${requiredList}.`;
  }
  const allowedG = d.allowedGroupings as string[] | undefined;
  const allowedC = d.allowedColumns as string[] | undefined;
  const allowedD = d.allowedDisplayColumns as string[] | undefined;
  if (allowedG?.length) {
    return `Unknown column "${alias}". Valid dimensions: ${resolveLabels(
      m,
      allowedG,
      'grouping',
    ).join(', ')}.`;
  }
  if (allowedC?.length) {
    return `Unknown metric "${alias}". Valid options: ${resolveLabels(m, allowedC, 'stat').join(
      ', ',
    )}.`;
  }
  if (allowedD?.length) {
    return `Unknown column "${alias}". Valid options: ${resolveLabels(m, allowedD, 'display').join(
      ', ',
    )}.`;
  }
  if (d.requires) return `The column "${alias}" requires ${d.requires}.`;
  if (d.numerator ?? d.denominator) {
    const num = d.numerator ? resolveLabel(m, d.numerator as string, 'stat') : d.numerator;
    const den = d.denominator ? resolveLabel(m, d.denominator as string, 'stat') : d.denominator;
    return num && den
      ? `The ratio column "${alias}" requires both "${num}" and "${den}" to be selected.`
      : `The ratio column "${alias}" requires missing dependencies.`;
  }
  return `Invalid column "${alias}".`;
}

function formatDomainError(
  code: string | undefined,
  details: unknown,
  m?: AnalyticsStatMetadata,
): string | null {
  if (!code) return null;

  if (code === 'CHILD_LIMIT_EXCEEDS_PARENT_CEILING' && Array.isArray(details)) {
    const excessIds = details.flatMap((entry) => {
      if (!entry || typeof entry !== 'object') return [];
      const row = entry as { excessCatalogIds?: unknown };
      return Array.isArray(row.excessCatalogIds)
        ? row.excessCatalogIds.filter((id): id is string => typeof id === 'string')
        : [];
    });
    const uniqueIds = [...new Set(excessIds)];
    return uniqueIds.length
      ? `Some columns exceed the parent account ceiling: ${uniqueIds.join(
          ', ',
        )}. Remove them and save again.`
      : 'Some columns exceed the parent account ceiling. Remove them and save again.';
  }

  if (!details || typeof details !== 'object' || Array.isArray(details)) return null;
  const d = details as Record<string, unknown>;

  switch (code) {
    case 'CATALOG_IDS_OUTSIDE_CEILING': {
      const rejected = d.rejectedCatalogIds;
      if (Array.isArray(rejected) && rejected.length) {
        return `These columns are not allowed on the parent account: ${rejected.join(', ')}.`;
      }
      return 'One or more columns are outside the parent account ceiling.';
    }
    case 'INVALID_COLUMN':
      return formatInvalidColumn(d, m);
    case 'INVALID_GROUP_BY': {
      const key = (d.key as string) ?? 'group_by';
      const allowed = d.allowed as string[] | undefined;
      return allowed?.length
        ? `Invalid dimension "${key}". Allowed: ${resolveLabels(m, allowed, 'grouping').join(
            ', ',
          )}.`
        : `Invalid dimension "${key}".`;
    }
    case 'BASE_DIMENSION_MIX':
    case 'BASE_ENTITY_MIX': {
      const dims = (d.baseDimensions ?? d.baseEntities) as string[] | undefined;
      const label = code === 'BASE_DIMENSION_MIX' ? 'Base dimensions' : 'Base entities';
      return dims?.length
        ? `${label} are not mixable: ${dims.join(', ')}.`
        : `${label} are not mixable.`;
    }
    case 'VIEW_NOT_SUBSET':
      return (d.reason as string) ?? 'View selection is not a subset of its constraint.';
    case 'INVALID_OWNER':
    case 'NOT_FOUND':
    case 'DELETED':
    case 'INVALID_VIEW_TYPE':
    case 'SUBSET_VIOLATION':
    case 'INVALID_SELECTION_JSON':
      return null;
    default:
      return null;
  }
}

function formatZodErrors(errors: ZodValidationErrors): string {
  const parts: string[] = [];
  for (const [field, messages] of Object.entries(errors)) {
    if (messages?.length) {
      const humanField = field.replace(/\./g, ' → ');
      parts.push(`${humanField}: ${messages.join('; ')}`);
    }
  }
  return parts.join('\n') || 'Validation failed.';
}

/** Flatten nested Run { success: false, error?, errors? } to flat shape. Use in hooks before throwing. */
export function flattenAnalyticsStatsErrorResponse(data: unknown): unknown {
  if (!data || typeof data !== 'object') return data;
  if (!isNestedRun(data)) return data;

  const n = data as NestedRunResponse;
  if (n.error != null && typeof n.error === 'object' && !Array.isArray(n.error)) {
    return n.error;
  }
  if (n.errors != null && typeof n.errors === 'object') {
    return { errors: n.errors };
  }
  return data;
}

/** Parse API error and return user-friendly message. Hooks flatten nested Run responses first. */
export function formatAnalyticsStatsApiError(
  data: unknown,
  options?: { metadata?: AnalyticsStatMetadata; fallbackMessage?: string },
): string {
  const fallback = options?.fallbackMessage ?? 'Something went wrong. Please try again.';
  if (!data || typeof data !== 'object') return fallback;
  const obj = data as object;

  if (isErrorObjectString(obj)) return obj.error;
  if (isZodValidationErrors(obj)) return formatZodErrors(obj.errors);
  if (isDomainError(obj)) {
    const formatted = formatDomainError(obj.code, obj.details, options?.metadata);
    if (formatted) return formatted;
    if (typeof obj.message === 'string') return obj.message;
  }
  if ('message' in obj && typeof (obj as { message: unknown }).message === 'string') {
    return (obj as { message: string }).message;
  }
  return fallback;
}

/** Error thrown by analytics stats APIs, carries raw response for format-at-render */
export class AnalyticsStatsApiError extends Error {
  readonly rawResponse: unknown;

  /** Set by {@link jobTerminalFailureError} so poll queries skip retry. */
  isTerminalJobFailure?: boolean;

  constructor(rawResponse: unknown) {
    super('Analytics stats API error');
    this.name = 'AnalyticsStatsApiError';
    this.rawResponse = rawResponse;
  }
}

interface ErrorWithRawResponse {
  rawResponse: unknown;
}

function hasRawResponse(error: unknown): error is ErrorWithRawResponse {
  if (error == null || typeof error !== 'object' || !('rawResponse' in error)) {
    return false;
  }
  return (error as ErrorWithRawResponse).rawResponse !== undefined;
}

function getRawResponse(error: unknown): unknown {
  if (error instanceof AnalyticsStatsApiError) return error.rawResponse;
  if (hasRawResponse(error)) return error.rawResponse;
  return undefined;
}

/**
 * Get a user-friendly error message for display. Use at render time so metadata
 * and structured details are available for formatting and potential UI actions.
 */
export function getAnalyticsStatsErrorMessage(
  error: unknown,
  options?: {
    metadata?: AnalyticsStatMetadata;
    fallbackMessage?: string;
  },
): string {
  const raw = getRawResponse(error);
  if (raw !== undefined) {
    return formatAnalyticsStatsApiError(raw, options);
  }
  if (error instanceof Error) return error.message;
  return options?.fallbackMessage ?? 'Something went wrong. Please try again.';
}

/** User-facing message when a stats report file download fails (saved or ad-hoc views). */
export function getAnalyticsStatDownloadErrorMessage(
  error: unknown,
  metadata?: AnalyticsStatMetadata,
): string {
  return getAnalyticsStatsErrorMessage(error, {
    metadata,
    fallbackMessage: 'Failed to download. Please try again.',
  });
}
